import React, { useEffect } from "react";
import { useLocation, Navigate, useNavigate, Link } from "react-router-dom";
import { Check, ArrowRight, Phone, Sparkles, ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getService, services } from "@/data/services";
import useSEO from "@/hooks/useSEO";

export default function ServiceLanding() {
  const location = useLocation();
  const navigate = useNavigate();
  const slug = location.pathname.replace(/^\//, "");
  const service = getService(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useSEO({
    title: service?.seoTitle,
    description: service?.seoDescription,
    keywords: service?.keywords,
    canonicalPath: `/${slug}`
  });

  if (!service) {
    return <Navigate to="/" replace />;
  }

  const goToContact = () => {
    navigate("/");
    setTimeout(() => {
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 300);
  };

  const otherServices = services.filter((s) => s.slug !== slug);

  return (
    <div data-testid={`service-landing-${slug}`} className="min-h-screen bg-[#0A0A0A] text-white">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[70vh] min-h-[480px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={service.hero} alt={`${service.name} Elysium Events Sibiu`} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0A0A0A]" />
        </div>
        <div className="relative text-center px-4 max-w-4xl mx-auto space-y-6">
          <span className="text-[11px] font-semibold tracking-[0.35em] text-[#D4AF37] uppercase flex items-center gap-2 justify-center">
            <Sparkles size={13} /> Elysium Events · Sibiu
          </span>
          <h1 className="font-serif-lux text-4xl sm:text-5xl lg:text-6xl text-white font-medium leading-tight">
            {service.name}
          </h1>
          <p className="text-lg sm:text-xl text-[#F3E5D8] font-light italic">{service.tagline}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <button
              data-testid="service-cta-offer"
              onClick={goToContact}
              className="bg-[#D4AF37] hover:bg-[#C5A880] text-black text-xs font-semibold tracking-widest uppercase px-8 py-4 transition-all duration-300 hover:scale-105 flex items-center justify-center gap-2"
            >
              Solicită ofertă <ArrowRight size={15} />
            </button>
            <a
              href="tel:0720820028"
              data-testid="service-cta-call"
              className="border border-[#D4AF37]/40 text-white hover:border-[#D4AF37] text-xs font-semibold tracking-widest uppercase px-8 py-4 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Phone size={15} /> 0720 820 028
            </a>
          </div>
        </div>
      </section>

      {/* Intro + Benefits */}
      <section className="py-20 sm:py-28 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          <div className="space-y-6">
            <span className="text-xs font-semibold tracking-[0.3em] text-[#D4AF37] uppercase">De ce Elysium Events?</span>
            <h2 className="font-serif-lux text-3xl sm:text-4xl text-white font-medium leading-tight">
              {service.name} de neuitat, în inima Sibiului
            </h2>
            <div className="w-20 h-[1px] bg-[#D4AF37]/50" />
            <p className="text-white/60 font-light leading-relaxed">{service.intro}</p>
          </div>
          <div className="space-y-4">
            {service.benefits.map((b) => (
              <div key={b} className="flex items-start gap-4 p-5 bg-[#121212] border border-[#D4AF37]/10 hover:border-[#D4AF37]/40 transition-colors">
                <span className="p-1.5 bg-[#D4AF37]/15 text-[#D4AF37] rounded-full shrink-0 mt-0.5"><Check size={15} /></span>
                <span className="text-sm text-white/80 font-light">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="pb-20 sm:pb-28 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 space-y-3">
          <span className="text-xs font-semibold tracking-[0.3em] text-[#D4AF37] uppercase">Galerie</span>
          <h2 className="font-serif-lux text-3xl sm:text-4xl text-white font-medium">Momente din locația noastră</h2>
          <div className="w-20 h-[1px] bg-[#D4AF37]/50 mx-auto" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {service.galleryImages.map((img, i) => (
            <div key={i} className="aspect-[4/5] overflow-hidden border border-[#D4AF37]/10 group">
              <img src={img} alt={`${service.name} Sibiu - imagine ${i + 1}`} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700" />
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-r from-[#1a1608] via-[#2a2109] to-[#1a1608] border-y border-[#D4AF37]/30">
        <div className="max-w-3xl mx-auto text-center px-4 space-y-6">
          <h2 className="font-serif-lux text-3xl sm:text-4xl text-white font-medium">
            Rezervă acum data evenimentului tău!
          </h2>
          <p className="text-white/60 font-light">
            Contactează-ne astăzi și descoperă cum putem transforma {service.name.toLowerCase()} tale într-o experiență memorabilă.
          </p>
          <button
            data-testid="service-cta-bottom"
            onClick={goToContact}
            className="bg-[#D4AF37] hover:bg-[#C5A880] text-black text-xs font-semibold tracking-widest uppercase px-10 py-4 transition-all duration-300 hover:scale-105 inline-flex items-center gap-2"
          >
            Solicită ofertă <ArrowRight size={15} />
          </button>
        </div>
      </section>

      {/* Other services links (SEO internal linking) */}
      <section className="py-16 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-8">
          <h3 className="font-serif-lux text-2xl text-white">Alte servicii</h3>
          <Link to="/" className="text-[#D4AF37] hover:text-white text-xs uppercase tracking-widest flex items-center gap-1 transition-colors">
            <ArrowLeft size={14} /> Înapoi acasă
          </Link>
        </div>
        <div className="flex flex-wrap gap-3">
          {otherServices.map((s) => (
            <Link
              key={s.slug}
              to={`/${s.slug}`}
              data-testid={`other-service-${s.slug}`}
              className="px-5 py-2.5 bg-[#121212] border border-[#D4AF37]/15 hover:border-[#D4AF37]/50 text-sm text-white/70 hover:text-[#D4AF37] transition-colors"
            >
              {s.name}
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
